import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ActionMenu, CardList, ConfirmDialog, Badge } from '@/components/ui';
import { Eye, Pencil, Trash2 } from 'lucide-react';

interface WorkOrdersTableProps {
  items: any[];
  onDelete?: (id: string) => void;
}

export function WorkOrdersTable({ items, onDelete }: WorkOrdersTableProps) {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [deleteItem, setDeleteItem] = useState<any>(null);

  const detailPath = (id: string) => `/work-orders/detail/${id}`;
  const editPath = (id: string) => `/work-orders/edit/${id}`;

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'info';
      case 'paused': return 'warning';
      case 'overdue': return 'danger';
      case 'not_started': return 'secondary';
      default: return 'secondary';
    }
  };

  const getPriorityVariant = (priority: string) => {
    switch (priority) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'secondary';
    }
  };

  const getTypeVariant = (type: string) => {
    return type === 'maintenance' ? 'success' : 'info';
  };

  const handleDeleteConfirm = () => {
    if (deleteItem) {
      onDelete?.(deleteItem.id);
      setDeleteItem(null);
    }
  };

  return (
    <>
      <div className="bg-white rounded-md border border-slate-100">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse sm:text-sm text-xs">
            <thead>
              <tr className="border-b border-slate-50 text-slate-400">
                <th className="p-3 w-8 font-normal"><input type="checkbox" className="rounded border-slate-200" /></th>
                <th className="p-3 text-center w-60 min-w-[180px] font-normal">Công việc</th>
                <th className="p-3 min-w-[120px] font-normal">Người phụ trách</th>
                <th className="p-3 min-w-[100px] font-normal">Hạn hoàn thành</th>
                <th className="p-3 min-w-[100px] font-normal">Trạng thái</th>
                <th className="p-3 min-w-[80px] font-normal">Ưu tiên</th>
                <th className="p-3 min-w-[120px] font-normal">Tiến độ</th>
                <th className="p-3 text-right min-w-[80px] font-normal">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-slate-50 last:border-b-0 hover:bg-slate-50/50 transition-colors">
                  <td className="p-3"><input type="checkbox" className="rounded border-slate-200" /></td>
                  <td className="p-3 text-left">
                    <div className="text-slate-800 leading-tight font-normal">{item.title}</div>
                    <div className="sm:text-sm text-xs font-normal mt-1 text-blue-600">[{item.code || 'WO-0000'}]</div>
                  </td>
                  <td className="p-3 text-left">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-xs text-slate-500 overflow-hidden">
                        {item.leadAvatar ? <img src={item.leadAvatar} className="w-full h-full object-cover" /> : (item.leadName?.[0] || '?')}
                      </div>
                      <span className="text-slate-800 whitespace-nowrap">{item.leadName}</span>
                    </div>
                  </td>
                  <td className="p-3 text-left">
                    <div className="text-slate-700">{item.dueDate || '25/05/2024'}</div>
                    {item.status === 'overdue' && (
                      <div className="text-rose-600 sm:text-sm text-xs mt-1">Quá hạn 2 ngày</div>
                    )}
                  </td>
                  <td className="p-3 text-left">
                    <Badge variant={getStatusVariant(item.status) as any} className="whitespace-nowrap font-normal">
                      {item.status === 'completed' ? 'Hoàn thành' :
                        item.status === 'in_progress' ? 'Đang thực hiện' :
                          item.status === 'paused' ? 'Tạm dừng' :
                            item.status === 'overdue' ? 'Quá hạn' : 'Chưa bắt đầu'}
                    </Badge>
                  </td>
                  <td className="p-3 text-left">
                    <Badge variant={getPriorityVariant(item.priority) as any} className="whitespace-nowrap font-normal">
                      {item.priority === 'high' ? 'Cao' : item.priority === 'medium' ? 'Trung bình' : 'Thấp'}
                    </Badge>
                  </td>
                  <td className="p-3 w-32 text-left">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 flex-1 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${item.status === 'completed' ? 'bg-emerald-600' : 'bg-blue-600'}`}
                          style={{ width: `${item.progress}%` }}
                        />
                      </div>
                      <span className="text-slate-700 min-w-[30px]">{item.progress}%</span>
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <ActionMenu
                      items={[
                        { label: 'Chi tiết', icon: Eye, onClick: () => navigate(detailPath(item.id)) },
                        { label: 'Chỉnh sửa', icon: Pencil, onClick: () => navigate(editPath(item.id)) },
                        { label: 'Xóa', icon: Trash2, onClick: () => setDeleteItem(item), destructive: true },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile View */}
        <div className="md:hidden">
          <CardList 
            items={items}
            gridClassName="grid-cols-1 gap-2"
            className="p-2"
            renderCard={(item) => (
              <div key={item.id} className="p-3 rounded-md border border-slate-100 bg-white space-y-3">
                <div className="flex justify-between items-start gap-4">
                  {/* Left part: Nội dung */}
                  <div className="flex-1 space-y-1">
                    <div className="text-slate-900 sm:text-sm text-xs leading-relaxed">
                      <span className="font-medium text-blue-600">[{item.code || 'WO-0000'}]</span> - {item.title}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="w-5 h-5 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-[10px] text-slate-500 overflow-hidden">
                        {item.leadAvatar ? <img src={item.leadAvatar} className="w-full h-full object-cover" /> : (item.leadName?.[0] || '?')}
                      </div>
                      <span className="text-[10px] sm:text-xs text-slate-600">{item.leadName}</span>
                      <span className="text-slate-300">•</span>
                      <span className="text-[10px] sm:text-xs text-slate-500">{item.dueDate || '25/05/2024'}</span>
                    </div>
                  </div>
                  
                  {/* Right part: Stacked badges */}
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <Badge variant={getStatusVariant(item.status) as any} className="whitespace-nowrap font-normal">
                      {item.status === 'completed' ? 'Hoàn thành' :
                        item.status === 'in_progress' ? 'Đang thực hiện' :
                          item.status === 'paused' ? 'Tạm dừng' :
                            item.status === 'overdue' ? 'Quá hạn' : 'Chưa bắt đầu'}
                    </Badge>
                    <Badge variant={getPriorityVariant(item.priority) as any} className="whitespace-nowrap font-normal">
                      {item.priority === 'high' ? 'Cao' : item.priority === 'medium' ? 'Trung bình' : 'Thấp'}
                    </Badge>
                  </div>
                </div>

                {/* Bottom Actions */}
                <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-50">
                  <span className="text-slate-800 sm:text-sm text-xs mr-auto font-medium">Tiến độ: {item.progress}%</span>
                  <button onClick={() => navigate(detailPath(item.id))} className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors" title="Chi tiết">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button onClick={() => navigate(editPath(item.id))} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title="Chỉnh sửa">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => setDeleteItem(item)} className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors" title="Xóa">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          />
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!deleteItem}
        title="Xác nhận xóa"
        message={
          <>
            Bạn có chắc chắn muốn xóa lệnh thi công <span className="font-normal text-slate-900">{deleteItem?.title}</span>?
            Hành động này không thể hoàn tác.
          </>
        }
        confirmText="Xác nhận xóa"
        isDestructive
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteItem(null)}
      />
    </>
  );
}
